/**
 * CodeMirror 6 wrapper used for memory + YAML editors (§4.4.4).
 *
 * - Markdown or YAML language based on `language` prop
 * - `value` is the "controlled" buffer; we only dispatch transactions
 *   back when the prop changes externally (e.g. after a save refetch)
 * - Parent owns dirty-state tracking; we don't use CM's built-in history
 *   for that purpose — just report changes via `onChange`
 * - Imperative ref exposes `save()` so parents can wire ⌘S at the
 *   editor scope without leaking CM types
 */
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { EditorState } from '@codemirror/state';
import { EditorView, keymap, lineNumbers, highlightActiveLineGutter } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { markdown } from '@codemirror/lang-markdown';
import { yaml } from '@codemirror/lang-yaml';
import { oneDark } from '@codemirror/theme-one-dark';
import { cn } from '@/lib/utils';

export type MemoryEditorLanguage = 'markdown' | 'yaml';

export interface MemoryEditorHandle {
  save: () => void;
  focus: () => void;
  getValue: () => string;
}

export interface MemoryEditorProps {
  value: string;
  onChange: (next: string) => void;
  language?: MemoryEditorLanguage;
  readOnly?: boolean;
  onSave?: () => void;
  className?: string;
}

export const MemoryEditor = forwardRef<MemoryEditorHandle, MemoryEditorProps>(
  function MemoryEditor(
    { value, onChange, language = 'markdown', readOnly = false, onSave, className },
    ref
  ) {
    const hostRef = useRef<HTMLDivElement | null>(null);
    const viewRef = useRef<EditorView | null>(null);
    const onChangeRef = useRef(onChange);
    const onSaveRef = useRef(onSave);

    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
      onSaveRef.current = onSave;
    }, [onSave]);

    // Rebuild the editor when `language` or `readOnly` changes. Otherwise
    // we update content in-place via dispatch to preserve cursor state.
    useEffect(() => {
      if (!hostRef.current) return;

      const languageExt = language === 'yaml' ? yaml() : markdown();

      const state = EditorState.create({
        doc: value,
        extensions: [
          lineNumbers(),
          highlightActiveLineGutter(),
          history(),
          keymap.of([
            ...defaultKeymap,
            ...historyKeymap,
            {
              key: 'Mod-s',
              preventDefault: true,
              run: () => {
                onSaveRef.current?.();
                return true;
              },
            },
          ]),
          languageExt,
          oneDark,
          EditorView.editable.of(!readOnly),
          EditorView.updateListener.of((update) => {
            if (update.docChanged) {
              onChangeRef.current(update.state.doc.toString());
            }
          }),
          EditorView.theme({
            '&': {
              height: '100%',
              fontSize: '13px',
            },
            '.cm-scroller': {
              fontFamily:
                "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            },
          }),
        ],
      });

      const view = new EditorView({ state, parent: hostRef.current });
      viewRef.current = view;

      return () => {
        view.destroy();
        viewRef.current = null;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language, readOnly]);

    // Sync external value changes (e.g. load after refetch) without
    // nuking the undo history on every keystroke.
    useEffect(() => {
      const view = viewRef.current;
      if (!view) return;
      const current = view.state.doc.toString();
      if (current !== value) {
        view.dispatch({
          changes: { from: 0, to: current.length, insert: value },
        });
      }
    }, [value]);

    useImperativeHandle(
      ref,
      () => ({
        save: () => onSaveRef.current?.(),
        focus: () => viewRef.current?.focus(),
        getValue: () => viewRef.current?.state.doc.toString() ?? '',
      }),
      []
    );

    return (
      <div
        ref={hostRef}
        className={cn('h-full w-full overflow-hidden rounded-md border', className)}
        role="textbox"
        aria-label="Code editor"
      />
    );
  }
);
